import { DateTime } from "luxon";

// How many calendar days back the bell keeps: today + the previous 6. Must
// match RETENTION_DAYS in backend_v2/domain/notifications/service.py — the
// server deletes anything older, this only decides how the rest is laid out.
export const RETENTION_DAYS = 7;

/**
 * Lay the bell's notifications out for display.
 *
 * Returns `{ unread, today, days }`:
 *  - `unread`  every unread notification in the window, ANY day, newest first —
 *              pinned on top so the red dot always points at something visible.
 *  - `today`   READ notifications from today, newest first.
 *  - `days`    the previous days (at most RETENTION_DAYS - 1 = 6), newest day
 *              first, each `{ key, label, items }` holding that day's READ
 *              notifications. Days with nothing to show are omitted.
 *
 * Days are the VIEWER's calendar days (`zone`, an IANA name), not UTC — a 9 PM
 * Pacific notification belongs to that evening, not to tomorrow. Anything before
 * the window is dropped defensively (the server already excludes it).
 *
 * Pure: takes `now` so it is unit-testable and never reads the clock itself.
 */
export function groupNotifications(items, now, zone) {
  const nowZ = DateTime.fromMillis(+now, { zone });
  const today = nowZ.startOf("day");
  const windowStart = today.minus({ days: RETENTION_DAYS - 1 });

  const inWindow = (items || [])
    .map((n) => ({ n, at: DateTime.fromISO(n.created_at, { zone }) }))
    .filter(({ at }) => at.isValid && at >= windowStart)
    .sort((a, b) => b.at.toMillis() - a.at.toMillis());

  const unread = inWindow.filter(({ n }) => !n.read_at).map(({ n }) => n);
  const readToday = [];
  const byDay = new Map();

  for (const { n, at } of inWindow) {
    if (n.read_at) {
      if (at >= today) {
        readToday.push(n);
      } else {
        const key = at.toISODate();
        if (!byDay.has(key)) byDay.set(key, { key, at, items: [] });
        byDay.get(key).items.push(n);
      }
    }
  }

  const days = [...byDay.values()]
    .sort((a, b) => b.at.toMillis() - a.at.toMillis())
    .map(({ key, at, items: dayItems }) => ({
      key,
      label: dayLabel(at, today),
      items: dayItems,
    }));

  return { unread, today: readToday, days };
}

function dayLabel(at, today) {
  const daysAgo = Math.round(today.diff(at.startOf("day"), "days").days);
  return daysAgo === 1 ? "Yesterday" : at.toFormat("ccc, LLL d");
}
